import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { lastNameSortKey } from "../../utility/bowlerName";
import { clampTicketCount } from "../../utility/brackets/entryTickets";

const chipClass = (active) =>
  `max-lg:min-h-11 max-lg:inline-flex max-lg:items-center px-3 py-1.5 text-sm rounded-lg font-display font-bold ${
    active
      ? "bg-brand-charcoal text-brand-cream"
      : "bg-white text-brand-charcoal border border-brand-charcoal/20"
  }`;

const fieldClass =
  "w-full px-4 py-3 border border-brand-charcoal/20 rounded-lg bg-white text-brand-charcoal focus:ring-2 focus:ring-brand-gold-ink focus:border-transparent";

const laneSortValue = (bowler) => {
  const lane = Number(bowler?.lane);
  return Number.isInteger(lane) && lane > 0 ? lane : Number.POSITIVE_INFINITY;
};

const laneIsSet = (bowler) => Number.isFinite(laneSortValue(bowler));

const BowlerMeta = ({ bowler }) => (
  <div className="min-w-0 flex-1">
    <div className="font-medium text-brand-charcoal truncate">{bowler.name}</div>
    <div className="text-xs text-brand-muted">
      {bowler.lane ? `Lane ${bowler.lane}` : "Lane not set"}
    </div>
  </div>
);

const Stepper = ({ bowler, count, disabled, onChange }) => (
  <div className="flex items-center gap-2 shrink-0">
    <button
      type="button"
      className="h-11 w-11 rounded-lg border border-brand-charcoal/20 bg-white text-xl font-display font-bold disabled:opacity-40"
      disabled={disabled || count === 0}
      onClick={(event) => {
        event.stopPropagation();
        onChange(bowler, count - 1);
      }}
      aria-label={`Remove a ticket for ${bowler.name}`}
    >
      −
    </button>
    <div className="w-8 text-center font-display font-bold text-lg">{count}</div>
    <button
      type="button"
      className="h-11 w-11 rounded-lg bg-brand-charcoal text-brand-cream text-xl font-display font-bold disabled:opacity-40"
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onChange(bowler, count + 1);
      }}
      aria-label={`Add a ticket for ${bowler.name}`}
    >
      +
    </button>
  </div>
);

const SellBoard = ({
  bowlers,
  tickets,
  search,
  onSearch,
  onCountChange,
  onDraftChange,
  disabled,
  canSell = true,
  flushRef,
}) => {
  const searchRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [sortBy, setSortBy] = useState("name");
  const [laneFilter, setLaneFilter] = useState("");
  const draftRef = useRef(null);
  const onCountChangeRef = useRef(onCountChange);
  const onDraftChangeRef = useRef(onDraftChange);
  const bowlersRef = useRef(bowlers);
  onCountChangeRef.current = onCountChange;
  onDraftChangeRef.current = onDraftChange;
  bowlersRef.current = bowlers;

  const countByBowler = useMemo(() => {
    const map = new Map();
    (tickets || []).forEach((ticket) => {
      map.set(ticket.bowlerID, (map.get(ticket.bowlerID) || 0) + 1);
    });
    return map;
  }, [tickets]);

  const savedCount = useCallback(
    (bowlerId) => countByBowler.get(bowlerId) || 0,
    [countByBowler]
  );

  const countFor = (bowlerId) =>
    draft?.bowlerId === bowlerId ? draft.count : savedCount(bowlerId);

  const flushDraft = useCallback(() => {
    const pending = draftRef.current;
    if (!pending || pending.count === pending.baseline) {
      draftRef.current = null;
      if (pending) {
        setDraft(null);
        onDraftChangeRef.current?.(null);
      }
      return Promise.resolve();
    }
    const bowler = bowlersRef.current.find((row) => row.id === pending.bowlerId);
    const count = pending.count;
    draftRef.current = null;
    if (!bowler) {
      setDraft(null);
      onDraftChangeRef.current?.(null);
      return Promise.resolve();
    }
    const write = Promise.resolve(onCountChangeRef.current(bowler, count));
    setDraft(null);
    onDraftChangeRef.current?.(null);
    return write;
  }, []);

  useEffect(() => {
    if (flushRef) flushRef.current = flushDraft;
    return () => {
      flushDraft();
      if (flushRef) flushRef.current = null;
    };
  }, [flushDraft, flushRef]);

  const openRow = (bowlerId) => {
    const count = savedCount(bowlerId);
    const next = { bowlerId, count, baseline: count };
    draftRef.current = next;
    setSelectedId(bowlerId);
    setDraft(next);
    onDraftChangeRef.current?.({ bowlerId, count });
  };

  const leaveRow = () => {
    flushDraft();
    setSelectedId(null);
  };

  const selectBowler = (bowlerId) => {
    if (selectedId === bowlerId) {
      leaveRow();
      return;
    }
    flushDraft();
    openRow(bowlerId);
  };

  const stepCount = (bowler, nextCount) => {
    const count = clampTicketCount(nextCount);
    const next = {
      bowlerId: bowler.id,
      count,
      baseline: draftRef.current?.baseline ?? savedCount(bowler.id),
    };
    draftRef.current = next;
    setDraft(next);
    onDraftChangeRef.current?.({ bowlerId: bowler.id, count });
  };

  const laneOptions = useMemo(() => {
    const lanes = new Set();
    let hasUnset = false;
    (bowlers || []).forEach((bowler) => {
      if (laneIsSet(bowler)) {
        lanes.add(Number(bowler.lane));
      } else {
        hasUnset = true;
      }
    });
    return {
      lanes: [...lanes].sort((left, right) => left - right),
      hasUnset,
    };
  }, [bowlers]);

  const query = String(search || "").trim().toLowerCase();

  const rows = useMemo(() => {
    const filtered = (bowlers || []).filter((bowler) => {
      if (laneFilter === "unset" && laneIsSet(bowler)) return false;
      if (
        laneFilter &&
        laneFilter !== "unset" &&
        String(bowler.lane) !== laneFilter
      ) {
        return false;
      }
      if (!query) return true;
      const name = bowler.name?.toLowerCase() || "";
      return name.includes(query);
    });
    return filtered.sort((left, right) => {
      if (sortBy === "lane") {
        const laneDiff = laneSortValue(left) - laneSortValue(right);
        if (laneDiff !== 0) return laneDiff;
      }
      return lastNameSortKey(left.name).localeCompare(
        lastNameSortKey(right.name)
      );
    });
  }, [bowlers, query, sortBy, laneFilter]);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    if (rows.length === 1 && selectedId !== rows[0].id) {
      flushDraft();
      openRow(rows[0].id);
    }
  }, [query, laneFilter, rows]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!bowlers?.length) {
    return (
      <p className="text-sm text-brand-charcoal">
        Nobody is on this squad yet. Add teams on Scoring first.
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="sticky top-0 z-10 bg-brand-cream/95 py-2 space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          <input
            ref={searchRef}
            type="search"
            value={search}
            onChange={(event) => onSearch(event.target.value)}
            placeholder="Search name"
            aria-label="Search name"
            className={fieldClass}
          />
          <select
            value={laneFilter}
            onChange={(event) => setLaneFilter(event.target.value)}
            aria-label="Filter by lane"
            className={fieldClass}
          >
            <option value="">All lanes</option>
            {laneOptions.hasUnset ? (
              <option value="unset">Lane not set</option>
            ) : null}
            {laneOptions.lanes.map((lane) => (
              <option key={lane} value={String(lane)}>
                {`Lane ${lane}`}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-display font-bold uppercase tracking-wider text-brand-muted">
            Sort
          </span>
          <button
            type="button"
            className={chipClass(sortBy === "name")}
            aria-pressed={sortBy === "name"}
            onClick={() => setSortBy("name")}
          >
            Name
          </button>
          <button
            type="button"
            className={chipClass(sortBy === "lane")}
            aria-pressed={sortBy === "lane"}
            onClick={() => setSortBy("lane")}
          >
            Lane
          </button>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-brand-muted">
          No bowlers match those filters.
        </p>
      ) : (
        <ul className="divide-y divide-brand-charcoal/10">
          {rows.map((bowler) => {
            const count = countFor(bowler.id);
            const selected = selectedId === bowler.id;
            return (
              <li key={bowler.id}>
                <div
                  className={`w-full flex items-center gap-3 py-3 px-2 rounded-lg ${
                    selected
                      ? "bg-brand-gold/25 ring-1 ring-brand-charcoal/20"
                      : "hover:bg-white"
                  }`}
                >
                  <button
                    type="button"
                    className="min-w-0 flex-1 flex items-center gap-3 text-left"
                    onClick={() => {
                      if (!canSell || disabled) return;
                      selectBowler(bowler.id);
                    }}
                  >
                    <BowlerMeta bowler={bowler} />
                  </button>
                  {selected && canSell && !disabled ? (
                    <Stepper
                      bowler={bowler}
                      count={count}
                      disabled={disabled}
                      onChange={stepCount}
                    />
                  ) : count > 0 ? (
                    <span className="font-display font-bold text-brand-charcoal">
                      {count}
                    </span>
                  ) : null}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SellBoard;
